import { useStyles } from './SessionHeader.styles';
import { Box } from '@mui/material';
import { ProfileInfo } from '../../profile-info';
import { Text } from '../../../atoms/typography';
import cx from 'clsx';
import { convertSecondToTime } from '../../../../../utils/CommonUtils';
import { useEffect, useRef, useState } from 'react';
import { btnType, Button } from '../../../atoms/button';
import { ProgressBar } from '../../../atoms/ProgressBar';
import { Menu } from '../../../molecules/menu';
import { ConfirmationModal } from '../../../templates/confirmation-modal';
import { useDispatch, useSelector } from 'react-redux';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import { appointmentActionCreators } from '../../../../../redux/modules/appointment';
import dayjs, { getUserTimeZone } from '../../../../../utils/dayjs';
import history from '../../../../../utils/history';
import AppointmentScheduling from '../../../../../pages/provider/appointments/components/appointment-scheduling';
import AppointmentCancel from '../../../../../pages/provider/appointments/components/appointment-cancel';

const SessionHeader = ({ 
  appointment, 
  isSessionStarted, 
  isEndedSession, 
  onDurationUpdated, 
  onEndSession, 
  startSignoff, 
  goToSchedule,
  showMarkAsNoShow = false,
}) => {
  const classes = useStyles({ isEndedSession });
  const dispatch = useDispatch();
  const { meta } = useSelector(getAuth);
  const [serviceDuration, setServiceDuration] = useState(0);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const serviceCountdownRef = useRef(null);

  useEffect(() => {
    let duration = serviceDuration;
    setServiceDuration(0);
    if (isSessionStarted) {
      serviceCountdownRef.current = setInterval(() => {
        duration++;
        setServiceDuration(duration);
        if (onDurationUpdated) {
          onDurationUpdated(duration);
        }
        const percentage = (duration / (appointment?.serviceDuration * 60)) * 100;
        if (percentage <= 100) {
          setProgressBarValue(percentage);
        } else {
          setProgressBarValue(100);
        }

        if (isEndedSession) {
          clearInterval(serviceCountdownRef.current);
        }
      }, 1000);
    }
    return () => clearInterval(serviceCountdownRef.current);
  }, [isSessionStarted]);

  useEffect(() => {
    if (isEndedSession && serviceCountdownRef.current) {
      clearInterval(serviceCountdownRef.current);
    }
  }, [isEndedSession]);

  const handleMarkAsNoShow = () => {
    setShowNoShowModal(true);
  };

  const editAppointmentClick = () => {
    setShowReschedule(true);
  };

  const cancelAppointmentClick = () => {
    setShowCancel(true);
  };

  const onCloseSlider = () => {
    showReschedule && setShowReschedule(false);
    showCancel && setShowCancel(false);
    showNoShowModal && setShowNoShowModal(false);
  };

  const updateAppointment = payload => {
    history.push(
      `/provider/${meta?.userId}/day-schedule?date=${dayjs().tz(getUserTimeZone()).format('YYYY-MM-DD')}`,
    );
  };

  const handleConfirmMarkNoShow = () => {
    dispatch(
      appointmentActionCreators.cancelAppointment({
        appointmentId: appointment.appointmentId,
        appointmentCancelParams: { reason: 'NO_SHOW' },
        callback: errorApi => {
          if (errorApi) {
            console.log(errorApi);
          } else {
            history.push(
              `/provider/${meta?.userId}/day-schedule?date=${dayjs()
                .tz(getUserTimeZone())
                .format('YYYY-MM-DD')}`,
            );
          }
        },
      }),
    );
    setShowNoShowModal(false);
  };

  const handleCancelMarkNoShow = () => {
    setShowNoShowModal(false);
  };

  // Build menu items
  const menuItems = [];
  
  menuItems.push({
    label: 'Reschedule Appointment',
    onClick: editAppointmentClick,
  });
  
  if (!showMarkAsNoShow) {
    menuItems.push({
      label: 'Mark as no show',
      onClick: handleMarkAsNoShow,
    });
  }
  
  menuItems.push({
    label: 'Cancel Appointment',
    onClick: cancelAppointmentClick,
  });

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.container}>
        {/* Header content */}
        <ProfileInfo
          type="member"
          photo={appointment.participantImage}
          nickName={`${appointment.patientFirstName} ${appointment.patientLastName}`}
          fullName={appointment.serviceName}
          uuid={appointment.patientUuid}
        />
        <Box className={classes.rightActions}>
          {isEndedSession ? (
            <Button className={classes.signoffBtn} variant={btnType.OUTLINE} onClick={goToSchedule}>
              Leave session
            </Button>
          ) : (
            <>
              <Text className={classes.time}>
                <span>
                  <Text
                    className={cx(
                      serviceDuration > appointment?.serviceDuration * 60 ? classes.timeRed : classes.time,
                    )}
                  >
                    {convertSecondToTime(serviceDuration)}
                  </Text>
                </span>
                {` / ${appointment?.serviceDuration || 0} min`}
              </Text>
              <Button className={classes.endSessionBtn} onClick={onEndSession}>
                End Session
              </Button>
              {menuItems.length > 0 && (
                <Menu
                  icon="more-vertical"
                  items={menuItems}
                   className={classes.menu}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      <ProgressBar value={progressBarValue} className={classes.progressBar} />

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showNoShowModal}
        onClose={handleCancelMarkNoShow}
        onSave={handleConfirmMarkNoShow}
        title="Mark as no show?"
        message={`Are you sure you want to mark ${
          appointment?.patientFirstName || 'this member'
        } as no show? This action cannot be undone.`}
        noLabel="Cancel"
        yesLabel="Mark as no show"
      />
      <AppointmentScheduling
        hideCancelBtn
        open={showReschedule}
        onClose={onCloseSlider}
        updateAppointment={updateAppointment}
        appointment={appointment}
      />
      <AppointmentCancel
        open={showCancel}
        onClose={onCloseSlider}
        appointment={appointment}
        updateAppointment={updateAppointment}
      />
    </Box>
  );
};

export { SessionHeader };
